import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Users, 
  Shield, 
  Search, 
  Filter,
  Eye,
  Edit,
  Plus,
  Crown,
  AlertTriangle,
  Check
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { 
  Permission, 
  Role, 
  ROLE_PERMISSIONS 
} from '@/types/permissions';
import { StaffUser } from '@/types';
import { ProtectedComponent } from '@/components/common/ProtectedComponent';
import { clsx } from 'clsx';
import { api } from '@/services/api';
import { logger } from '@/utils/logger';

interface StaffPermissionListProps {
  onViewUser?: (user: StaffUser) => void;
  onEditUser?: (user: StaffUser) => void;
}

export const StaffPermissionList: React.FC<StaffPermissionListProps> = ({
  onViewUser,
  onEditUser,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role | 'all'>('all');
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStaffUsers = async () => {
      setLoading(true);
      const result = await api.staff.getAll();
      if (result.error || !result.data) {
        logger.error('Failed to fetch staff users:', result.error);
        setError(t('errors.serverError'));
        setStaffUsers([]);
      } else {
        setError(null);
        setStaffUsers(result.data);
      }
      setLoading(false);
    };

    loadStaffUsers();
  }, [t]);

  // 过滤用户
  const filteredUsers = staffUsers.filter(user => {
    const matchesSearch = 
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    
    const matchesStatus = showInactiveUsers || user.is_active;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleColor = (role: Role) => {
    switch (role) {
      case Role.ADMIN: return 'text-red-600 bg-red-100';
      case Role.MANAGER: return 'text-purple-600 bg-purple-100';
      case Role.SUPERVISOR: return 'text-blue-600 bg-blue-100';
      case Role.STAFF: return 'text-green-600 bg-green-100';
      case Role.TRAINEE: return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUserPermissionCount = (user: StaffUser) => {
    const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
    const customPermissions = user.permissions || [];
    const totalPermissions = [...new Set([...rolePermissions, ...customPermissions])];
    return totalPermissions.length;
  };

  if (!hasPermission(Permission.VIEW_STAFF)) {
    return (
      <div className="text-center py-12">
        <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('settings.accessRestricted')}
        </h3>
        <p className="text-gray-600">
          {t('settings.noPermissionToViewStaff')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 搜索和过滤器 */}
      <div className="card card-comfortable">
        <div className="flex flex-col md:flex-row gap-4">
          {/* 搜索框 */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('settings.searchStaffNameEmailUsername')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* 角色过滤器 */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as Role | 'all')}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('settings.allRoles')}</option>
              {Object.values(Role).map(role => (
                <option key={role} value={role}>
                  {t(`debug.roles.${role}`)} - {t(`permissionManager.${role}RoleDesc`)}
                </option>
              ))}
            </select>
          </div>

          {/* 状态切换 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactiveUsers}
              onChange={(e) => setShowInactiveUsers(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">{t('settings.showDisabledUsers')}</span>
          </label>
        </div>
      </div>

      {/* 员工列表 */}
      <div className="space-y-4">
        {error && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="card card-comfortable">
            <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 card card-comfortable">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('settings.noStaffFound')}
            </h3>
            <p className="text-gray-600">
              {t('settings.tryAdjustingSearchOrFilters')}
            </p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card card-comfortable hover:shadow-medium transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* 用户头像 */}
                  <div className={clsx(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    user.is_active 
                      ? "bg-gradient-primary text-white" 
                      : "bg-gray-300 text-gray-600"
                  )}>
                    <span className="font-bold">
                      {user.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* 用户信息 */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {user.full_name}
                      </h3>
                      <span className={clsx(
                        "px-2 py-1 rounded-full text-xs font-medium capitalize",
                        getRoleColor(user.role)
                      )}>
                        {t(`debug.roles.${user.role}`)}
                      </span>
                      {!user.is_active && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {t('staff.inactive')}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{user.email}</span>
                      <span>•</span>
                      <span>{getUserPermissionCount(user)} {t('staff.permissions')}</span>
                      <span>•</span>
                      <span>
                        {t('settings.lastLogin')}: {user.last_login_at 
                          ? new Date(user.last_login_at).toLocaleDateString('zh-CN')
                          : t('permissionManager.neverLoggedIn')
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* 操作按钮 */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onViewUser?.(user)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title={t('common.viewDetails')}
                  >
                    <Eye className="h-4 w-4" />
                  </button>

                  <ProtectedComponent permission={Permission.MANAGE_STAFF}>
                    <button
                      onClick={() => onEditUser?.(user)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title={t('settings.permissions')}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </ProtectedComponent>
                </div>
              </div>

              {/* 权限概览（简化版） */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-500" />
                    <span className="text-gray-600">
                      {t('staff.rolePermissions')}: {(ROLE_PERMISSIONS[user.role] || []).length} {t('staff.permissions')}
                    </span>
                  </div>
                  
                  {((user.permissions || []) as Permission[]).length > 0 && (
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4 text-green-500" />
                      <span className="text-gray-600">
                        {t('staff.extraPermissions')}: {((user.permissions || []) as Permission[]).length} {t('staff.permissions')}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 ml-auto">
                    {user.is_active ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-gray-600">
                      {user.is_active ? t('staff.normal') : t('staff.inactive')}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* 统计信息 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card card-comfortable text-center">
          <p className="text-2xl font-bold text-blue-600">
            {staffUsers.length}
          </p>
          <p className="text-sm text-gray-600">{t('staff.totalStaff')}</p>
        </div>
        
        <div className="card card-comfortable text-center">
          <p className="text-2xl font-bold text-green-600">
            {staffUsers.filter(u => u.is_active).length}
          </p>
          <p className="text-sm text-gray-600">{t('staff.activeStaff')}</p>
        </div>
        
        <div className="card card-comfortable text-center">
          <p className="text-2xl font-bold text-amber-600">
            {staffUsers.filter(u => [Role.ADMIN, Role.MANAGER].includes(u.role)).length}
          </p>
          <p className="text-sm text-gray-600">{t('staff.managers')}</p>
        </div>
        
        <div className="card card-comfortable text-center">
          <p className="text-2xl font-bold text-gray-600">
            {filteredUsers.length}
          </p>
          <p className="text-sm text-gray-600">{t('staff.filterResults')}</p>
        </div>
      </div>
    </div>
  );
};

export default StaffPermissionList;
